<template>
  <div>
    <v-card outlined class="pa-4 mb-4">
      <div class="d-flex align-start flex-wrap mb-3" style="gap: 12px;">
        <div>
          <div class="pw-section">MRR día a día · {{ monthLabel }}</div>
          <div class="caption grey--text text--darken-1">
            MRR en USD del total B2C/B2B2C (B2C + B2B2C + Upsell) al cierre de cada día
          </div>
        </div>
        <v-spacer />
        <v-select
          v-model="year"
          :items="years"
          label="Año"
          outlined
          dense
          hide-details
          class="period-filter"
        />
        <v-select
          v-model="month"
          :items="monthOptions"
          label="Mes"
          outlined
          dense
          hide-details
          class="period-filter"
        />
      </div>

      <div v-if="loading" class="d-flex justify-center py-10">
        <v-progress-circular indeterminate color="primary" />
      </div>
      <v-alert v-else-if="error" type="error" dense outlined>{{ error }}</v-alert>
      <div v-else-if="!days.length" class="text-center grey--text py-10">Sin datos para este mes</div>
      <template v-else>
        <line-chart :chart-data="chartData" :options="chartOptions" :styles="chartStyles" />
        <p class="caption grey--text text--darken-1 mt-3 mb-0">
          Haz clic en un punto para ver las altas y bajas de ese día.
          El MRR usa los precios y el tipo de cambio de hoy: la línea muestra cambios de cartera, no de precio.
        </p>
      </template>
    </v-card>

    <B2CV2BarDetail
      v-if="detail.selection"
      :selection="detail.selection"
      :rows="detail.rows"
      :loading="detail.loading"
      :error="detail.error"
      @close="detail.selection = null"
    />
  </div>
</template>

<script>
import { Line } from 'vue-chartjs';
import { TREND_COLORS } from '@/components/B2CTrendChart.vue';
import B2CV2BarDetail from '@/components/b2c-report-v2/B2CV2BarDetail.vue';
import B2CReportV2Service from '@/services/b2c-report-v2.service';
import { formatNumber, signed } from '@/utils/b2c-report.utils';

// El historial de estados está completo desde julio de 2026: antes no hay serie diaria confiable.
const FIRST_MONTH = '2026-07';

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const LineChart = {
  extends: Line,
  props: ['chartData', 'options'],
  mounted() {
    this.renderChart(this.chartData, this.options);
  },
  watch: {
    chartData: {
      deep: true,
      handler(newData) {
        if (this.$data._chart) this.$data._chart.destroy();
        this.renderChart(newData, this.options);
      }
    }
  }
};

const emptyDetail = () => ({ selection: null, rows: [], loading: false, error: '' });

export default {
  name: 'B2CV2DailyPanel',
  components: { LineChart, B2CV2BarDetail },
  props: {
    country: { type: String, default: null },
    // Hoy en Chile, 'YYYY-MM-DD'.
    today: { type: String, required: true }
  },
  data() {
    return {
      year: Number(this.today.slice(0, 4)),
      month: Number(this.today.slice(5, 7)),
      days: [],
      loading: false,
      error: '',
      detail: emptyDetail()
    };
  },
  computed: {
    years() {
      const first = Number(FIRST_MONTH.slice(0, 4));
      const last = Number(this.today.slice(0, 4));
      return Array.from({ length: last - first + 1 }, (_, i) => first + i);
    },
    /** Meses del año elegido entre julio de 2026 y el mes en curso. */
    monthOptions() {
      const current = this.today.slice(0, 7);
      return MONTH_NAMES.map((text, i) => ({ text, value: i + 1 })).filter((m) => {
        const key = this.monthKey(this.year, m.value);
        return key >= FIRST_MONTH && key <= current;
      });
    },
    selectedMonth() {
      return this.monthKey(this.year, this.month);
    },
    monthLabel() {
      return `${MONTH_NAMES[this.month - 1]} ${this.year}`;
    },
    chartStyles() {
      return { height: '300px', position: 'relative' };
    },
    chartData() {
      return {
        labels: this.days.map((d) => Number(d.date.slice(8, 10))),
        datasets: [
          {
            label: 'MRR (USD)',
            data: this.days.map((d) => d.mrrUsd),
            borderColor: TREND_COLORS.primary,
            backgroundColor: 'rgba(16, 75, 148, 0.08)',
            pointBackgroundColor: TREND_COLORS.primary,
            pointRadius: 4,
            pointHoverRadius: 6,
            borderWidth: 2,
            fill: true,
            lineTension: 0.25
          }
        ]
      };
    },
    // Rango con colchón: con pocos días el autoescalado queda casi plano (mismo criterio que MrrDailyPanel).
    yAxisRange() {
      const values = this.days.map((d) => d.mrrUsd);
      const min = Math.min(...values);
      const max = Math.max(...values);
      const padding = Math.max((max - min) * 0.15, max * 0.02, 50);
      return { min: Math.max(0, Math.floor(min - padding)), max: Math.ceil(max + padding) };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{ gridLines: { display: false }, scaleLabel: { display: true, labelString: 'Día' } }],
          yAxes: [
            {
              ticks: { ...this.yAxisRange, maxTicksLimit: 6, callback: (v) => `USD ${formatNumber(v)}` },
              gridLines: { color: 'rgba(0,0,0,0.05)' }
            }
          ]
        },
        tooltips: {
          displayColors: false,
          callbacks: {
            title: (items) => this.displayDate(this.days[items[0].index].date),
            label: (item) => {
              const d = this.days[item.index];
              return [
                `MRR: USD ${formatNumber(d.mrrUsd)}`,
                `Activas: ${formatNumber(d.activeEnd)}`,
                `Altas: ${formatNumber(d.activations)} · Bajas: ${formatNumber(d.churns)} (neto ${signed(d.activations - d.churns)})`
              ];
            }
          }
        },
        hover: { onHover: (evt, elements) => { evt.target.style.cursor = elements.length ? 'pointer' : 'default'; } },
        onClick: (evt, elements) => {
          if (elements && elements.length) this.openDay(this.days[elements[0]._index]);
        }
      };
    }
  },
  watch: {
    year() {
      // Al cambiar de año, el mes elegido puede quedar fuera de rango: se toma el último disponible.
      if (!this.monthOptions.some((m) => m.value === this.month)) {
        this.month = this.monthOptions[this.monthOptions.length - 1].value;
        return;
      }
      this.loadSeries();
    },
    month: 'loadSeries',
    country: 'loadSeries'
  },
  created() {
    this.loadSeries();
  },
  methods: {
    monthKey(year, month) {
      return `${year}-${String(month).padStart(2, '0')}`;
    },
    displayDate(value) {
      const [y, m, d] = value.split('-');
      return `${d}/${m}/${y}`;
    },
    async loadSeries() {
      const { selectedMonth: month, country } = this;
      this.detail = emptyDetail();
      this.loading = true;
      this.error = '';
      const result = await B2CReportV2Service.getDaily({ month, country });
      // Si cambiaron de mes o de país mientras cargaba, esta respuesta ya no aplica.
      if (month !== this.selectedMonth || country !== this.country) return;
      this.loading = false;
      if (result.status !== 'ok') {
        this.days = [];
        this.error = result.message;
        return;
      }
      this.days = result.data.days;
    },
    /** Lista las altas y bajas del día. */
    async openDay(day) {
      if (!day) return;
      const selection = { date: day.date };
      this.detail = { selection, rows: [], loading: true, error: '' };
      const result = await B2CReportV2Service.getDailyDetail({ date: day.date, country: this.country });
      // Si eligieron otro día mientras cargaba, esta respuesta ya no aplica.
      if (this.detail.selection !== selection) return;
      this.detail.loading = false;
      if (result.status !== 'ok') {
        this.detail.error = result.message;
        return;
      }
      this.detail.rows = result.data;
    }
  }
};
</script>

<style scoped>
.period-filter {
  max-width: 160px;
}
</style>
